import { useState, type FormEvent } from "react";
import type { ConexaoDeImpressora } from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import {
  Botao,
  Campo,
  Entrada,
  Falha,
  Girando,
  Segmentos,
  Selecao,
  Selo,
  Vazio,
  cx,
} from "../../components/ui.js";
import { DownloadsDoAgente } from "./DownloadsDoAgente.js";
import { MolduraDoPainel, Nota } from "./MolduraDoPainel.js";
import { EstadoDaImpressora } from "./EstadoDaImpressora.js";
import {
  agenteLigadoDepoisDe,
  mensagemDoErro,
  validadeDoCodigo,
  validarFormulario,
  type ImpressoraDaTela,
  type ValoresDoFormulario,
} from "./impressora.js";
import { comVerbo } from "./tempo.js";
import { useAgora } from "./useFilaDeImpressao.js";

/**
 * Impressoras, para o admin: cadastrar, testar, editar, remover — e, pelo agente do notebook do evento (#109),
 * ligar e desligar o agente.
 *
 * Só o admin cadastra, e isso é regra do servidor (na direta, ele faz requisição ao endereço informado), não só
 * da tela: esconder o botão de quem não é admin é cortesia, o `adminProcedure` é quem barra.
 *
 * O cliente dono da impressora vem de `users.list`, a mesma lista que a TV da galeria e a concessão de
 * cortesia usam. Cadastrada, a impressora direta é testada na hora: o admin fica sabendo na mesma tela se o
 * sistema a alcança, em vez de descobrir quando um operador tentar imprimir.
 */
export function PainelDeImpressoras({ aoFechar }: { aoFechar: () => void }) {
  const utils = trpc.useUtils();
  const agora = useAgora();

  /** `null` é a lista; `{}` é o formulário de cadastro; com `editando`, o de edição. */
  const [formulario, setFormulario] = useState<{ editando?: ImpressoraDaTela } | null>(null);
  /** O painel do código que liga o agente, com a hora em que ele foi gerado. */
  const [ligando, setLigando] = useState<{
    impressoraId: number;
    codigo: string;
    expiraEm: string;
    geradoEm: Date;
  } | null>(null);
  const [removendo, setRemovendo] = useState<number | null>(null);
  const [desligando, setDesligando] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const impressoras = trpc.impressao.impressoras.useQuery(undefined, {
    staleTime: 0,
    refetchOnMount: "always",
    // Com o código na tela, a lista se atualiza sozinha: é por ela que se vê o agente chegar. Fora disso, o
    // estado do agente muda devagar e a tela não precisa perguntar a cada instante.
    refetchInterval: ligando ? 3_000 : 15_000,
  });
  const usuarios = trpc.users.list.useQuery();
  const clientes = (usuarios.data ?? []).filter((u) => u.role === "cliente");

  const testar = trpc.impressao.testarImpressora.useMutation({
    onSettled: () => void utils.impressao.impressoras.invalidate(),
    onError: (e) => setErro(mensagemDoErro(e, "Não foi possível testar a impressora.")),
  });
  const testandoId = testar.isPending ? (testar.variables?.id ?? null) : null;

  const remover = trpc.impressao.removerImpressora.useMutation({
    onSuccess: () => {
      setRemovendo(null);
      setAviso("Impressora removida.");
      void utils.impressao.impressoras.invalidate();
      void utils.impressao.trabalhos.invalidate();
    },
    // O CONFLICT ("há impressão em andamento…") já vem em frase que diz o que fazer.
    onError: (e) => {
      // A confirmação fecha: o motivo da recusa é o que a pessoa precisa ler agora, e ele fica no topo.
      setRemovendo(null);
      setErro(mensagemDoErro(e, "Não foi possível remover a impressora."));
    },
  });

  const ligar = trpc.impressao.ligarAgente.useMutation({
    onSuccess: (r, { id }) => {
      setErro(null);
      setLigando({
        impressoraId: id,
        codigo: r.codigo,
        expiraEm: String(r.expiraEm),
        geradoEm: new Date(),
      });
    },
    onError: (e) => setErro(mensagemDoErro(e, "Não foi possível gerar o código do agente.")),
  });

  const desligar = trpc.impressao.desligarAgente.useMutation({
    onSuccess: () => {
      setDesligando(null);
      setAviso("Agente desligado. O notebook não imprime mais para esta impressora.");
      void utils.impressao.impressoras.invalidate();
    },
    onError: (e) => {
      setDesligando(null);
      setErro(mensagemDoErro(e, "Não foi possível desligar o agente."));
    },
  });

  const lista = impressoras.data ?? [];

  if (ligando) {
    const imp = lista.find((i) => i.id === ligando.impressoraId);
    return (
      <MolduraDoPainel
        titulo="Ligar o agente"
        subtitulo={imp?.nome}
        aoFechar={aoFechar}
        rodape={
          <div className="flex flex-wrap gap-2 pb-1">
            <Botao tom="quieto" className="min-h-11" onClick={() => setLigando(null)}>
              Voltar às impressoras
            </Botao>
          </div>
        }
      >
        <LigarAgente
          codigo={ligando.codigo}
          expiraEm={ligando.expiraEm}
          ligou={agenteLigadoDepoisDe(imp?.agente, ligando.geradoEm)}
          maquina={imp?.agente?.maquina ?? null}
          agora={agora}
          gerandoOutro={ligar.isPending}
          aoGerarOutro={() => ligar.mutate({ id: ligando.impressoraId })}
        />
      </MolduraDoPainel>
    );
  }

  if (formulario) {
    return (
      <MolduraDoPainel
        titulo={formulario.editando ? "Editar impressora" : "Cadastrar impressora"}
        aoFechar={aoFechar}
      >
        <FormularioDaImpressora
          editando={formulario.editando}
          clientes={clientes.map((c) => ({ id: c.id, nome: c.name }))}
          carregandoClientes={usuarios.isLoading}
          aoSalvar={(id, conexao) => {
            setFormulario(null);
            setErro(null);
            void utils.impressao.impressoras.invalidate();
            if (conexao === "agente") {
              // Pelo agente, o passo seguinte é ligá-lo: o código já sai na mesma tela.
              setAviso(null);
              ligar.mutate({ id });
              return;
            }
            setAviso("Impressora salva. Conferindo se o sistema a alcança…");
            testar.mutate(
              { id },
              {
                // O cadastro diz logo o que importa: dá para imprimir daqui, ou só pelo app da Canon?
                onSuccess: (r) =>
                  setAviso(
                    r.alcancavel
                      ? "Impressora salva, e o sistema a alcança."
                      : "Impressora salva, mas o sistema não a alcança agora: a mensagem está no cartão dela.",
                  ),
              },
            );
          }}
          aoVoltar={() => setFormulario(null)}
        />
      </MolduraDoPainel>
    );
  }

  return (
    <MolduraDoPainel
      titulo="Impressoras"
      aoFechar={aoFechar}
      rodape={
        <div className="flex flex-wrap gap-2 pb-1">
          <Botao
            tom="brasa"
            className="min-h-11"
            onClick={() => {
              setErro(null);
              setAviso(null);
              setFormulario({});
            }}
          >
            Cadastrar impressora
          </Botao>
          <Botao tom="quieto" className="min-h-11" onClick={aoFechar}>
            Fechar
          </Botao>
        </div>
      }
    >
      <p className="text-[0.875rem] leading-relaxed text-texto-suave">
        No evento, quem imprime é o agente: um programa no notebook ligado à mesma rede da
        impressora. Sem ele, use Baixar ou Compartilhar na tela de impressão.
      </p>

      <section
        aria-labelledby="titulo-baixar-agente"
        className="flex flex-col gap-4 rounded-peca border border-borda bg-superficie px-4 py-4"
      >
        <div>
          <h3 id="titulo-baixar-agente" className="text-[1rem] text-texto">
            Baixar o agente de impressão
          </h3>
          <p className="mt-1 text-[0.8125rem] leading-relaxed text-texto-suave">
            Instale no notebook do evento. Ao abrir, ele pede o código que aparece em Ligar agente,
            no cartão da impressora.
          </p>
        </div>
        <DownloadsDoAgente />
      </section>

      {aviso && (
        <p role="status" className="text-[0.875rem] text-texto">
          {aviso}
        </p>
      )}
      {erro && <Falha mensagem={erro} />}

      {impressoras.isLoading ? (
        <Girando className="mx-auto my-8 size-6 text-texto-fraco" />
      ) : impressoras.isError && !impressoras.data ? (
        <Falha
          mensagem={mensagemDoErro(impressoras.error, "Não foi possível ver as impressoras.")}
          tentarNovamente={() => void impressoras.refetch()}
        />
      ) : lista.length === 0 ? (
        <Vazio
          titulo="Nenhuma impressora ainda"
          descricao="Cadastre a impressora de um cliente para ele imprimir direto da tela de impressão."
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {lista.map((i) => {
            const pelaAgente = i.conexao === "agente";
            return (
              <li
                key={i.id}
                className="flex flex-col gap-3 rounded-peca border border-borda bg-superficie px-4 py-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                  <p className="min-w-0 text-[1.0625rem] text-texto wrap-anywhere">{i.nome}</p>
                  <div className="flex flex-wrap gap-1.5">
                    <Selo tom="quieto">{pelaAgente ? "pelo agente" : "direta"}</Selo>
                    <Selo tom="quieto">{i.donoNome}</Selo>
                  </div>
                </div>

                <EstadoDaImpressora
                  estado={i.estado}
                  estadoEm={i.estadoEm}
                  conexao={i.conexao}
                  agente={i.agente}
                  verificando={testandoId === i.id}
                  agora={agora}
                />

                {pelaAgente && i.agente && (
                  <p className="text-[0.8125rem] leading-relaxed text-texto-suave wrap-anywhere">
                    Agente em {i.agente.maquina ?? "um notebook"}
                    {i.agente.impressoraNaRede &&
                      ` · ${i.agente.impressoraNaRede.nome} em ${i.agente.impressoraNaRede.endereco}`}
                    {i.agente.ligadoEm && ` · ${comVerbo("ligado", i.agente.ligadoEm, agora)}`}
                  </p>
                )}

                {!pelaAgente && i.ippUri && (
                  <p className="font-mono text-[0.75rem] leading-relaxed break-all text-texto-suave">
                    {i.ippUri}
                  </p>
                )}

                {removendo === i.id ? (
                  <Confirmacao
                    texto={`Remover “${i.nome}”? O histórico de impressões dela também some.`}
                    acao="Remover"
                    carregando={remover.isPending}
                    aoConfirmar={() => {
                      setErro(null);
                      remover.mutate({ id: i.id });
                    }}
                    aoVoltar={() => setRemovendo(null)}
                  />
                ) : desligando === i.id ? (
                  <Confirmacao
                    texto={`Desligar o agente de “${i.nome}”? O notebook para de imprimir para ela até ser ligado de novo, com outro código.`}
                    acao="Desligar"
                    carregando={desligar.isPending}
                    aoConfirmar={() => {
                      setErro(null);
                      desligar.mutate({ id: i.id });
                    }}
                    aoVoltar={() => setDesligando(null)}
                  />
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {pelaAgente && (
                      <Botao
                        tom={i.agente ? "quieto" : "brasa"}
                        className="min-h-11"
                        carregando={ligar.isPending && ligar.variables?.id === i.id}
                        disabled={ligar.isPending}
                        onClick={() => {
                          setErro(null);
                          setAviso(null);
                          ligar.mutate({ id: i.id });
                        }}
                      >
                        {i.agente ? "Ligar outro notebook" : "Ligar agente"}
                      </Botao>
                    )}
                    <Botao
                      tom="quieto"
                      className="min-h-11"
                      carregando={testandoId === i.id}
                      disabled={testar.isPending}
                      onClick={() => {
                        setErro(null);
                        setAviso(null);
                        testar.mutate({ id: i.id });
                      }}
                    >
                      {pelaAgente ? "Verificar" : "Testar"}
                    </Botao>
                    <Botao
                      tom="quieto"
                      className="min-h-11"
                      onClick={() => {
                        setErro(null);
                        setAviso(null);
                        setFormulario({ editando: i });
                      }}
                    >
                      Editar
                    </Botao>
                    {pelaAgente && i.agente && (
                      <Botao
                        tom="fantasma"
                        className="min-h-11"
                        onClick={() => {
                          setErro(null);
                          setAviso(null);
                          setDesligando(i.id);
                        }}
                      >
                        Desligar agente
                      </Botao>
                    )}
                    <Botao
                      tom="fantasma"
                      className="min-h-11"
                      onClick={() => {
                        setErro(null);
                        setAviso(null);
                        setRemovendo(i.id);
                      }}
                    >
                      Remover
                    </Botao>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </MolduraDoPainel>
  );
}

/** A confirmação de uma ação que não se desfaz, no lugar dos botões do cartão. */
function Confirmacao({
  texto,
  acao,
  carregando,
  aoConfirmar,
  aoVoltar,
}: {
  texto: string;
  acao: string;
  carregando: boolean;
  aoConfirmar: () => void;
  aoVoltar: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-peca border border-descartar/40 bg-descartar/10 px-3 py-3">
      <p className="text-[0.875rem] leading-relaxed text-texto">{texto}</p>
      <div className="flex flex-wrap gap-2">
        <Botao tom="descartar" className="min-h-11" carregando={carregando} onClick={aoConfirmar}>
          {acao}
        </Botao>
        <Botao tom="quieto" className="min-h-11" disabled={carregando} onClick={aoVoltar}>
          Voltar
        </Botao>
      </div>
    </div>
  );
}

/**
 * Ligar o agente: os passos, em ordem, e o código. É uma sequência de verdade (baixar, abrir, digitar, escolher),
 * por isso numerada. O código fica grande, para ser lido de longe por quem está no notebook do evento.
 */
function LigarAgente({
  codigo,
  expiraEm,
  ligou,
  maquina,
  agora,
  gerandoOutro,
  aoGerarOutro,
}: {
  codigo: string;
  expiraEm: string;
  ligou: boolean;
  maquina: string | null;
  agora: Date;
  gerandoOutro: boolean;
  aoGerarOutro: () => void;
}) {
  const validade = validadeDoCodigo(expiraEm, agora);

  if (ligou) {
    return (
      <div role="status" className="flex flex-col gap-2">
        <Selo tom="fixado" className="self-start">
          Agente ligado
        </Selo>
        <p className="text-[0.9375rem] leading-relaxed text-texto">
          O agente {maquina ? `em ${maquina} ` : ""}está conectado. Escolha a impressora na lista
          que ele mostrou; depois, é só deixar a janela dele aberta durante o evento.
        </p>
      </div>
    );
  }

  return (
    <ol className="flex flex-col gap-5">
      <li className="flex flex-col gap-2">
        <p className="text-[0.9375rem] text-texto">
          1. No notebook do evento, ligado à mesma rede da impressora, instale o agente:
        </p>
        <DownloadsDoAgente />
      </li>

      <li className="flex flex-col gap-1.5">
        <p className="text-[0.9375rem] text-texto">2. Abra o agente.</p>
        <p className="text-[0.8125rem] leading-relaxed text-texto-suave">
          No Windows, se aparecer “O Windows protegeu o computador”, toque em Mais informações e
          depois em Executar assim mesmo. Se o Firewall perguntar, permita o acesso à rede. No
          Linux, o comando já abre o agente; nas próximas vezes, rode ~/.local/bin/foto-agente.
        </p>
      </li>

      <li className="flex flex-col gap-2">
        <p className="text-[0.9375rem] text-texto">3. Digite este código quando o agente pedir:</p>
        <p
          className={cx(
            "font-display text-[2.25rem] leading-none tracking-[0.12em] tabular-nums",
            validade.vencido ? "text-texto-fraco line-through" : "text-texto",
          )}
          aria-label={`Código ${codigo.split("").join(" ")}`}
        >
          {codigo}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <p
            className={cx(
              "text-[0.8125rem]",
              validade.vencido ? "text-descartar" : "text-texto-suave",
            )}
          >
            {validade.texto} Ele serve uma vez só.
          </p>
          {validade.vencido && (
            <Botao
              tom="quieto"
              className="min-h-11"
              carregando={gerandoOutro}
              onClick={aoGerarOutro}
            >
              Gerar outro código
            </Botao>
          )}
        </div>
      </li>

      <li className="flex flex-col gap-1.5">
        <p className="text-[0.9375rem] text-texto">
          4. Escolha a impressora na lista que o agente mostrar.
        </p>
        <p className="flex items-center gap-2 text-[0.8125rem] text-texto-suave">
          <Girando className="size-3" />
          Esperando o agente se ligar…
        </p>
      </li>
    </ol>
  );
}

/**
 * Nome, como o sistema chega à impressora, o endereço (só na direta) e, ao cadastrar, o cliente.
 *
 * O endereço só é conferido aqui no tamanho; quem diz se serve é o servidor, e o motivo dele aparece
 * como veio ("só ipp://", "esse endereço aponta para a própria máquina"…). Trocar uma impressora de
 * cliente o servidor recusa, então na edição o cliente é só mostrado.
 */
function FormularioDaImpressora({
  editando,
  clientes,
  carregandoClientes,
  aoSalvar,
  aoVoltar,
}: {
  editando: ImpressoraDaTela | undefined;
  clientes: { id: number; nome: string }[];
  carregandoClientes: boolean;
  aoSalvar: (id: number, conexao: ConexaoDeImpressora) => void;
  aoVoltar: () => void;
}) {
  const [valores, setValores] = useState<ValoresDoFormulario>({
    nome: editando?.nome ?? "",
    // Nova, pelo agente: é o caminho do evento. A que já existia segue como estava.
    conexao: editando ? (editando.conexao ?? "direta") : "agente",
    ippUri: editando?.ippUri ?? "",
    donoId: "",
  });
  const [erros, setErros] = useState<{ nome?: string; ippUri?: string }>({});
  const [erro, setErro] = useState<string | null>(null);

  const salvar = trpc.impressao.salvarImpressora.useMutation({
    onSuccess: (r) => aoSalvar(r.id, valores.conexao),
    onError: (e) => setErro(mensagemDoErro(e, "Não foi possível salvar a impressora.")),
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    const r = validarFormulario(valores, editando ? { id: editando.id } : undefined);
    if (!r.ok) {
      setErros(r.erros);
      return;
    }
    setErros({});
    salvar.mutate(r.dados);
  }

  const mudouConexao = editando !== undefined && (editando.conexao ?? "direta") !== valores.conexao;

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-5">
      <Campo rotulo="Nome" erro={erros.nome ?? null} dica="Como a casa chama esta impressora.">
        <Entrada
          value={valores.nome}
          onChange={(e) => setValores({ ...valores, nome: e.target.value })}
          placeholder="Sala de eventos"
          maxLength={60}
          autoComplete="off"
          autoFocus
          aria-invalid={erros.nome ? true : undefined}
        />
      </Campo>

      <div className="flex flex-col gap-2">
        <Segmentos<ConexaoDeImpressora>
          rotulo="Como o sistema chega à impressora"
          valor={valores.conexao}
          opcoes={[
            { id: "agente", rotulo: "Pelo agente no notebook" },
            { id: "direta", rotulo: "Direto pelo endereço" },
          ]}
          aoMudar={(conexao) => setValores({ ...valores, conexao })}
        />
        <p className="text-[0.8125rem] leading-relaxed text-texto-suave">
          {valores.conexao === "agente"
            ? "O agente roda no notebook do evento, na mesma rede da impressora, e a acha sozinho — sem precisar do IP. Depois de salvar, aparece o código para ligá-lo."
            : "Só quando o próprio sistema roda na rede da impressora (ou tem VPN até ela). Pelo site, use o agente."}
        </p>
      </div>

      {valores.conexao === "direta" && (
        <Campo
          rotulo="Endereço"
          erro={erros.ippUri ?? null}
          dica="O endereço da impressora na rede onde o sistema roda."
        >
          <Entrada
            value={valores.ippUri}
            onChange={(e) => setValores({ ...valores, ippUri: e.target.value })}
            placeholder="ipp://192.168.3.243:631/ipp/print"
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            className="font-mono text-[0.9375rem]"
            aria-invalid={erros.ippUri ? true : undefined}
          />
        </Campo>
      )}

      {editando ? (
        <div>
          <p className="text-[0.8125rem] font-medium text-texto-suave">Cliente</p>
          <p className="mt-1 text-[1rem] text-texto">{editando.donoNome}</p>
          <p className="mt-1.5 text-[0.8125rem] text-texto-suave">
            Para passar a impressora a outro cliente, cadastre-a de novo para ele.
          </p>
        </div>
      ) : (
        <Campo
          rotulo="Cliente"
          dica={
            carregandoClientes
              ? "Carregando os clientes…"
              : "A impressora aparece só para este cliente e para quem trabalha para ele."
          }
        >
          <Selecao
            value={valores.donoId}
            onChange={(e) => setValores({ ...valores, donoId: e.target.value })}
          >
            <option value="">Você (suporte)</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </Selecao>
        </Campo>
      )}

      {editando && (mudouConexao || (valores.conexao === "direta" && editando.ippUri !== null)) && (
        <Nota tom="aviso">
          {mudouConexao && valores.conexao === "direta"
            ? "Passar para direta desliga o agente desta impressora. E não dá para mudar a conexão enquanto há impressão em andamento nela."
            : "Mudar o endereço ou a conexão faz a impressora ser verificada de novo. E não dá para mudá-los enquanto há impressão em andamento nela."}
        </Nota>
      )}

      {erro && <Falha mensagem={erro} />}

      <div className="flex flex-wrap gap-2 border-t border-borda pt-4">
        <Botao type="submit" tom="brasa" className="min-h-11" carregando={salvar.isPending}>
          {editando ? "Salvar" : "Cadastrar"}
        </Botao>
        <Botao
          type="button"
          tom="quieto"
          className="min-h-11"
          disabled={salvar.isPending}
          onClick={aoVoltar}
        >
          Voltar
        </Botao>
      </div>
    </form>
  );
}
